interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Stylized CO₂ / gas cylinder — tall body, valve cap, brand-band stripe.
 * Drawn at 64×96 viewBox so it scales cleanly to whatever px the tile uses.
 */
export function GasCylinderIcon({ size = 48, className }: IconProps) {
  // Maintain ~2:3 aspect.
  const w = Math.round(size * 0.66);
  const h = size;
  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 64 96"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      {/* Valve cap */}
      <rect x="26" y="2" width="12" height="6" rx="1.5" fill="currentColor" />
      {/* Valve handle wheel */}
      <circle cx="32" cy="11" r="3.5" fill="currentColor" />
      <rect x="31" y="8" width="2" height="6" fill="currentColor" />
      {/* Neck */}
      <rect x="28" y="14" width="8" height="6" fill="currentColor" />
      {/* Body — slightly rounded shoulders + base */}
      <path
        d="M14 24 Q14 20 18 20 H46 Q50 20 50 24 V88 Q50 92 46 92 H18 Q14 92 14 88 Z"
        fill="currentColor"
        opacity="0.9"
      />
      {/* Highlight stripe down the left for cylinder volume cue */}
      <rect x="18" y="26" width="3" height="60" fill="currentColor" opacity="0.35" />
      {/* Brand label band */}
      <rect x="14" y="50" width="36" height="14" fill="currentColor" opacity="0.55" />
      {/* "CO₂" mark on the band — white knockout */}
      <text
        x="32"
        y="61"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontWeight="700"
        fontSize="10"
        fill="white"
      >
        CO₂
      </text>
    </svg>
  );
}

/**
 * Pickup truck silhouette — used as the "we'll pick up your empties"
 * cue on Other-category cards.
 */
export function PickupTruckIcon({ size = 48, className }: IconProps) {
  const w = size;
  const h = Math.round(size * 0.66);
  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 96 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      {/* Truck bed (boxy, behind cab) */}
      <rect x="46" y="22" width="38" height="22" rx="2" fill="currentColor" opacity="0.9" />
      {/* Tailgate hinge line */}
      <line x1="46" y1="32" x2="46" y2="44" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" />
      {/* Cab roof — slanted forward */}
      <path
        d="M12 22 L26 10 H44 V22 Z"
        fill="currentColor"
        opacity="0.85"
      />
      {/* Cab body */}
      <rect x="10" y="22" width="36" height="22" rx="2" fill="currentColor" />
      {/* Window */}
      <path
        d="M16 22 L26 14 H40 V22 Z"
        fill="white"
        opacity="0.85"
      />
      {/* Window vertical mullion */}
      <line x1="29" y1="14" x2="29" y2="22" stroke="currentColor" strokeWidth="1.5" />
      {/* Door handle */}
      <rect x="34" y="32" width="6" height="2" rx="1" fill="white" opacity="0.6" />
      {/* Ground line */}
      <line x1="6" y1="52" x2="90" y2="52" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      {/* Front wheel */}
      <circle cx="22" cy="50" r="7" fill="currentColor" />
      <circle cx="22" cy="50" r="3" fill="white" opacity="0.85" />
      {/* Rear wheel */}
      <circle cx="68" cy="50" r="7" fill="currentColor" />
      <circle cx="68" cy="50" r="3" fill="white" opacity="0.85" />
      {/* Bumpers */}
      <rect x="6" y="40" width="6" height="4" fill="currentColor" opacity="0.6" />
      <rect x="84" y="36" width="4" height="8" fill="currentColor" opacity="0.6" />
    </svg>
  );
}
